<script setup>

    import { onMounted, onBeforeUnmount, onUnmounted, ref, computed, nextTick, watch } from 'vue'
    import { storeToRefs } from 'pinia'

    import LatteStyledContainer from '@/Components/App/LatteStyledContainer.vue'
    import LatteClientContacts from '@/Components/Contacts/LatteClientContacts.vue'
    import LatteProfileClientIntro from '@/Components/LatteProfileClientIntro.vue'
    import LatteClientSchedule from '@/Components/Client/LatteClientSchedule.vue'
    import LatteClientBasicInfo from '@/Components/Client/LatteClientBasicInfo.vue'
    import WeekPicker from '@/Components/WeekPicker.vue'
    import LatteClientCareNeed from '@/Components/Client/LatteClientCareNeed.vue'
    import LatteContainer from '@/Components/App/LatteContainer.vue'
    import LatteClientDocuments from '@/Components/Client/LatteClientDocuments.vue'
    import LatteClientDocumentsUploadDrawer from '@/Components/Client/LatteClientDocumentsUploadDrawer.vue'
    import LatteClientProfileDocumentAlerts from '@/Components/Client/LatteClientProfileDocumentAlerts.vue'
    import LatteFeedbackTableHeader from '@/Components/Feedback/LatteFeedbackTableHeader.vue'
    import LatteFeedbackTableClientRow from '@/Components/Feedback/LatteFeedbackTableClientRow.vue'
    import LatteFeedbackTableCaregiverRow from '@/Components/Feedback/LatteFeedbackTableCaregiverRow.vue'
    import Loader from '@/Components/App/Loader.vue'
    import LatteWhosNearby from '@/Components/Maps/LatteWhosNearby.vue'
    import LatteNotesActivity from '@/Components/Notes/LatteNotesActivity.vue'
    import { scrollToNavItemFromHash } from '@/Components/App/LattePageNav.vue'

    import { IconDismissCircle20Regular, IconSignature20Filled, IconQuestionCircle12Filled } from '@iconify-prerendered/vue-fluent'
    import { ChevronRightIcon } from '@heroicons/vue/24/solid'
    import { UserPlusIcon } from '@heroicons/vue/20/solid'

    import {
        useCaregiverStore,
        useClientSingleStore,
        useNotificationStore,
        useViewStore,
        useClientStore,
        useJobStore,
        useStaffStore,
        useConfigStore,
        useModuleLockStore,
        useNotesStore,
        notesSubject
    } from '@/Stores'

    import { useJobRunner } from '@/Helpers/jobRunner'
    import { useRoute, useRouter } from 'vue-router'
    import { util } from '@/Helpers'

    // ============================================
    // ROUTE & MODE DETECTION
    // ============================================

    const route = useRoute()
    const router = useRouter()

    // Mode is derived from the route, NOT from store state. The store starts as
    // defaultClient() which has no `id`, so a store-derived check reports "create mode"
    // on every fresh mount -- even on /client/:id -- and the client is never fetched.
    const isCreateMode = computed(() => route.name === 'client.create')

    const isEditMode = computed(() => !isCreateMode.value)

    const clientId = computed(() => {
        return Number(route.params.client) || client.value.id || null
    })

    // Before the client exists the notes module gets an unsaved subject (no id, no API calls); saving a note creates
    // the client first through the same first-save job as any other field (ensureClientPersisted).
    const clientNotesSubject = computed(() => notesSubject(clientId.value))

    // ============================================
    // STORE INITIALIZATION
    // ============================================

    const viewStore = useViewStore()
    const clientSingleStore = useClientSingleStore()
    const notesStore = useNotesStore()
    const caregiverStore = useCaregiverStore()
    const jobStore = useJobStore()
    const staffStore = useStaffStore()
    const clientStore = useClientStore()
    const configStore = useConfigStore()
    const notificationStore = useNotificationStore()
    const moduleLockStore = useModuleLockStore()

    const { loading, client } = storeToRefs(clientSingleStore)
    const { jobs, sortedJobsByClientAll, sortedJobsByClientAllOrdered } = storeToRefs(jobStore)
    const { caregivers } = storeToRefs(caregiverStore)

    // Module locks (only applied in create mode)
    const isTopLeftEnable = moduleLockStore.computedModule('topLeftModule')
    const isContactModuleEnable = moduleLockStore.computedModule('contactModule')

    // Helper computed for module lock application
    const profileClientIntroEnable = computed(() => {
        if (isEditMode.value) return true
        return isTopLeftEnable.value != false
    })

    const contactModuleEnable = computed(() => {
        if (isEditMode.value) return true
        return isContactModuleEnable.value
    })

    // ============================================
    // STATE & REFS
    // ============================================

    const scheduleDate = ref(util.date.lastSunday())
    const docsRef = ref(null)
    const createSignatureRequestButton = ref(null)
    const addNew = ref(false)
    const selectedContactId = ref(null)
    const scheduleBuilderMode = ref(null)

    // ============================================
    // JOB RUNNER SETUP
    // ============================================

    const jobRunner = useJobRunner('client')

    jobRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        clientSingleStore.stopInactivityTimeout()
    })

    jobRunner.subscribe('finishedJobSuccess', function() {
        clientSingleStore.resetInactivityRefreshTimer()
    })

    jobRunner.subscribe('addedJob', function() {
        clientSingleStore.resetInactivityRefreshTimer()
        notificationStore.removeById(clientSingleStore.getMaxRetryDialogId())
        clientSingleStore.removeMaxRetryDialogId()
    })

    jobRunner.subscribe('reachedMaxRetry', function() {
        const maxRetryDialogId = notificationStore.add('Error Saving Changes', 'error', 'Make an edit to try again.', 7 * 24 * 60 * 60 * 1000)
        clientSingleStore.setMaxRetryDialogId(maxRetryDialogId)
    })

    // ============================================
    // NAVIGATION ITEMS
    // ============================================

    const notesRef = ref(null)

    const navItems = computed(() => {
        let items = []
        items[0] = {
            id: 'client_intro',
            label: 'Summary'
        }
        items[1] = {
            id: 'basic_info',
            label: 'Basic Info'
        }
        items[2] = {
            id: 'contact',
            label: 'Contacts'
        }
        items[3] = {
            id: 'latte-map-nearby',
            label: "Who's Nearby"
        }
        items[4] = {
            id: 'care_need',
            label: 'Care Needs'
        }
        items[5] = {
            id: 'schedule',
            label: 'Care Schedule'
        }
        items[6] = {
            id: 'feedback',
            label: 'Caregiver Feedback'
        }
        items[7] = {
            id: 'documents',
            label: 'Documents'
        }

        // Notes & Activity is on the create view too (a note can create the client); `expand` opens it if collapsed,
        // `entryHash` lets timeline permalinks (#notes-<unix seconds>) open it as well
        items.splice(1, 0, {
            id: 'notes',
            label: 'Notes & Activity',
            expand: true,
            entryHash: true
        })

        return items
    })

    // Watch for changes to documentStatus and update nav items reactively
    watch(() => docsRef.value?.documentStatus, (newStatus) => {
        const { pageNavItems } = storeToRefs(viewStore)
        if (pageNavItems.value && pageNavItems.value.length > 0) {
            const documentsItem = pageNavItems.value.find(item => item.id === 'documents')
            if (documentsItem) {
                documentsItem.status = newStatus
            }
        }
    })

    // Navigate to edit mode once client is created (only when fe_generated is absent, i.e. client was hydrated from server)
    // Uses history.pushState to silently update URL without triggering Vue Router, keeping component state intact.
    // history.pushState does not update Vue Router's reactive route object, so we can't rely on
    // the `watch(() => route.params.client, ...)` below to fire after this — fetch directly instead.
    // hasHandledClientCreation guards against an infinite loop: loadClient() re-hydrates `client`,
    // which would otherwise re-trigger this same watcher forever (the fe_generated check alone
    // stays satisfied on every subsequent hydration of a server-persisted client).
    const hasHandledClientCreation = ref(false)
    watch(client, (newValue) => {
        if (!hasHandledClientCreation.value && route.name === 'client.create' && newValue.id && newValue.fe_generated == undefined) {
            hasHandledClientCreation.value = true
            history.pushState({}, null, `/client/${newValue.id}`)
            loadClient(newValue.id)
            rehydrateJobs()
        }
    })

    // ============================================
    // DATA HYDRATION FUNCTIONS
    // ============================================

    async function hydrateClient(data) {
        loading.value = true
        await clientSingleStore.hydrate(data)
        loading.value = false
    }

    async function hydrateCaregiversAll(data) {
        loading.value = true
        try {
            await caregiverStore.hydrateAllCaregivers(data)
        } finally {
            loading.value = false
        }
    }

    async function hydrateJobs(data, config = { start: 14, end: 1 }) {
        try {
            await jobStore.hydrate(data.jobs, config)
            await staffStore.hydrateFromJobs(Object.values(jobs.value))
            await caregiverStore.hydrateFromJobs(Object.values(jobs.value))
            await clientStore.hydrateFromJobs(Object.values(jobs.value))
        } finally {
            loading.value = false
        }
    }

    function rehydrateJobs(data) {
        const clientIdVal = clientId.value || route.params.client
        loading.value = true

        jobStore.index(null, null, null, true, clientIdVal, null)
            .then(response => hydrateJobs({ jobs: response.data }, { start: 14, end: 1, includeMissingStartDate: true }))
            .catch(() => {
                loading.value = false
                notificationStore.add('Error loading client jobs', 'error', 'Please refresh the page')
            })
    }

    function rehydrateClient(data) {
        loading.value = true
        return hydrateClient(data)
    }

    // 'documents' must stay in this list: it is also cached by the store as
    // cacheAdditionalFields and reused by the inactivity refresh, so dropping it
    // would make a later refresh hydrate a client without document_bundles.
    const CLIENT_ADDITIONAL_FIELDS = ['notes', 'disposition_history_summary', 'data_completion_status', 'documents']

    function loadClient(clientIdVal, hashContactId = null) {
        return clientSingleStore.getClientById(clientIdVal, CLIENT_ADDITIONAL_FIELDS)
            .then(response => {
                const hydrated = rehydrateClient(response.data)
                if (hashContactId) {
                    scrollToContactAndSelect(hashContactId)
                }
                return hydrated
            })
    }

    function refreshClient() {
        loadClient(route.params.client)
    }

    // /client/create and /client/:id both render this component and RouterView has no
    // :key, so navigating between them reuses the instance and onMounted does not run
    // again. Fetch the newly created client here so its server-side state -- including
    // document_bundles -- replaces the locally staged data.
    watch(() => route.params.client, (newClientId) => {
        if (newClientId) {
            loadClient(newClientId).then(scrollToHashSection)
            rehydrateJobs()
        }
    })

    // Section deep links pushed while this profile is already open (e.g. a Feedback History link) do not remount it
    watch(() => route.hash, (newHash) => {
        if (newHash) {
            scrollToHashSection()
        }
    })

    // ============================================
    // UPDATE & CREATE HANDLERS
    // ============================================

    const TIMELINE_CLIENT_FIELDS = ['type', 'disposition', 'disposition_reasons']

    function updateClient(data, formId) {
        if (!client.value.id) {
            return addUpdateClientJob(data, formId)
        }

        clientSingleStore.applyLocalChangeToActiveClient(data)
        jobRunner.addJob('update', client.value.uuid, data, [formId], async (data, formId, jobId) => {
            await clientSingleStore.updateClientUsingUUID(client.value.uuid, data, ['documents'], formId, jobId)
                .then(function (response) {
                    if (jobRunner.hasScheduleJob('update', client.value.uuid) == false) {
                        hydrateClient(response.data)
                    }
                    jobRunner.handleJobSuccess('update', client.value.uuid, jobId)
                    notificationStore.add('Changes Saved')
                    if (TIMELINE_CLIENT_FIELDS.some((field) => field in data)) {
                        notesStore.clientProfileChanged()
                    }
                })
                .catch(function(error) {
                    jobRunner.handleJobError('update', client.value.uuid, jobId, error.error.response?.status)
                })
        })
    }

    function storeClient(data, formId) {
        addUpdateClientJob(data, formId)
    }

    function addUpdateClientJob(data, formId, runNow = false) {
        const func = runNow ? 'addAndRunJob' : 'addJob'

        jobRunner[func]('update', client.value.uuid, data, [formId], async (data, formId, jobId) => {
            clientSingleStore.applyLocalChangeToActiveClient(data)
            const isFirstSave = !client.value.id
            const request = clientSingleStore.updateClientUsingUUID(client.value.uuid, data, [], formId, jobId)
            const handled = request
                .then(function (response) {
                    if (jobRunner.hasScheduleJob('update', client.value.uuid) == false) {
                        hydrateClient(response.data)
                    }
                    jobRunner.handleJobSuccess('update', client.value.uuid, jobId)
                    notificationStore.add('Changes Saved')
                })
                .catch(function(error) {
                    jobRunner.handleJobError('update', client.value.uuid, jobId, error?.error?.response?.status)
                })
            if (isFirstSave) {
                trackFirstSave(request, handled)
            }
            await handled
        })
    }

    function cancelClientUpdate(clientIdVal) {
        jobRunner.removeJob('update', clientIdVal)
    }

    // ============================================
    // CLIENT CREATION FROM THE NOTES MODULE
    // ============================================

    // A new client is created by its first saved field: the debounced 'update' job PATCHes /api/v2/clients/{uuid} and
    // the server creates the client when the uuid is unknown. The notes composer reuses that path (ensureClientPersisted),
    // so a note racing another field's first save, or a double click, shares ONE creation.
    let firstSaveOutcome = null
    let clientCreation = null
    let pendingClientCreation = null
    let createdClientId = null

    function persistedClientId() {
        return client.value.id || createdClientId || null
    }

    /**
     * Settles a pending creation from the first-save request's own outcome once its handling has finished, even when
     * the handling throws, so the creation never stays pending.
     *
     * @param {Promise<{ data?: { id?: number } }>} request the first-save PATCH
     * @param {Promise<unknown>} [handled] the handling of that request (hydrating, notifying)
     */
    function trackFirstSave(request, handled = request) {
        const outcome = Promise.allSettled([request, handled]).then(([saved]) => {
            if (saved.status === 'fulfilled') {
                settleClientCreation(saved.value?.data?.id ?? null)
            } else {
                settleClientCreation(null, saved.reason?.error ?? saved.reason)
            }
            if (firstSaveOutcome === outcome) {
                firstSaveOutcome = null
            }
        })
        firstSaveOutcome = outcome
    }

    function settleClientCreation(createdId, error = null) {
        if (createdId) {
            createdClientId = createdId
        }
        if (!pendingClientCreation) {
            return
        }
        const { resolve, reject } = pendingClientCreation
        pendingClientCreation = null
        if (createdId) {
            resolve(createdId)
        } else {
            reject(error ?? new Error('The client could not be created.'))
        }
    }

    /**
     * Awaits a first save already in flight; otherwise flushes the first-save job now (it merges with any field edit
     * still waiting).
     */
    async function createClientThroughFirstSave() {
        if (firstSaveOutcome) {
            await firstSaveOutcome
            if (persistedClientId()) {
                return persistedClientId()
            }
        }
        const created = new Promise((resolve, reject) => {
            pendingClientCreation = { resolve, reject }
        })
        addUpdateClientJob({ type: client.value.type, disposition: client.value.disposition }, 'notes', true)

        return await created
    }

    /**
     * Resolves with the persisted client id, creating the client through the first-save path when needed; concurrent
     * callers share one creation.
     *
     * @returns {Promise<number>}
     */
    function ensureClientPersisted() {
        const clientIdVal = persistedClientId()
        if (clientIdVal) {
            return Promise.resolve(clientIdVal)
        }
        if (!clientCreation) {
            clientCreation = createClientThroughFirstSave().finally(() => {
                clientCreation = null
            })
        }

        return clientCreation
    }

    // ============================================
    // CONTACT HANDLERS
    // ============================================

    function selectContact(contact) {
        selectedContactId.value = contact
    }

    function selectAddNewPrimaryContact() {
        clientSingleStore.changeAddNewPrimaryContactMode(true)
        addNew.value = false
        setTimeout(function() { addNew.value = true }, 0)
    }

    function getContactIdFromHash() {
        const hash = window.location.hash
        const match = hash.match(/^#contact-(\d+)$/)
        return match ? parseInt(match[1], 10) : null
    }

    function scrollToContactAndSelect(contactId = null) {
        nextTick(() => {
            util.scrollToElement('contact')
            if (contactId) {
                selectedContactId.value = contactId
            }
        })
    }

    /**
     * The hash handed to the notes module once its section is in view, so a timeline permalink scrolls to its entry
     * after (not before) the page has scrolled to the section.
     */
    const notesFocusHash = ref('')

    /**
     * Section deep link such as `/client/1#notes` or `#feedback`, opened like a page nav click once the client renders.
     */
    function scrollToHashSection() {
        nextTick(() => {
            scrollToNavItemFromHash(route.hash, navItems.value)
            notesFocusHash.value = route.hash
        })
    }

    // ============================================
    // LIFECYCLE HOOKS
    // ============================================

    onMounted(async () => {
        const client_id = route.params.client
        const hashContactId = getContactIdFromHash()

        viewStore.pageNavLoad(isCreateMode.value ? 'Create New Client' : 'Client Profile', navItems)

        caregiverStore.getAllCaregivers()
            .then(response => {
                hydrateCaregiversAll(response.data)
            })

        if (isCreateMode.value) {
            // CREATE MODE: Client starts with default state
            loading.value = false
            addNew.value = true
        } else {
            // EDIT MODE: Fetch existing client data
            loadClient(client_id, hashContactId).then(scrollToHashSection)

            rehydrateJobs()
        }
    })

    onBeforeUnmount(() => {
        clientSingleStore.stopInactivityTimeout()
    })

    onUnmounted(() => {
        loading.value = true
        jobStore.purge()
        viewStore.pageNavUnload()
        clientSingleStore.reset()
    })

</script>

<template>

    <div class="mx-auto max-w-7x md:px-8">
        <div class="mx-auto max-w-5xl">

            <!-- SECTION 1: SUMMARY -->
            <div
                id="client_intro"
                class="px-4 grid sm:grid-cols-1 gap-6 md:grid-cols-12"
            >
                <LatteProfileClientIntro
                    :enable="profileClientIntroEnable"
                    :isEdit="isCreateMode"
                    @client:store="storeClient"
                    @client:update="updateClient"
                    @client:cancel-update="cancelClientUpdate"
                    @contact:selected="selectContact"
                    @contact:addNewPrimary="selectAddNewPrimaryContact"
                />
            </div>

            <!-- Document alerts (edit mode only) -->
            <LatteClientProfileDocumentAlerts
                v-if="isEditMode && configStore.featureEnabled('client-document')"
                :show="configStore.featureEnabled('client-document')"
                :documents-module-ref="docsRef"
            />

            <!-- NOTES & ACTIVITY (also on the create view: a note can create the client) -->
            <LatteStyledContainer
                id="notes"
                title="Notes & Activity"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
                :centerHeaderSlot="false"
            >
                <template #header-actions>
                    <button
                        type="button"
                        data-test="notes-tour"
                        @click="notesRef && notesRef.startTour()"
                        class="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                        title="Start Notes & Activity tour"
                    >
                        <IconQuestionCircle12Filled class="size-5 text-white" />
                    </button>
                </template>
                <LatteNotesActivity
                    ref="notesRef"
                    :subject="clientNotesSubject"
                    :ensure-persisted="ensureClientPersisted"
                    :focus-hash="notesFocusHash"
                />
            </LatteStyledContainer>

            <!-- SECTION 2: BASIC INFO -->
            <LatteStyledContainer
                id="basic_info"
                title="Basic Info"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
            >
                <template #default>
                    <LatteClientBasicInfo
                        @client:update="updateClient"
                        @client:cancel-update="cancelClientUpdate"
                    />
                </template>
            </LatteStyledContainer>

            <!-- SECTION 3: CONTACTS -->
            <LatteStyledContainer
                id="contact"
                title="Contacts"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
                :centerHeaderSlot="false"
            >
                <template #header>
                    <button
                        class="rounded bg-white black py-1 flex space-x-1 items-center text-xs font-bold px-4"
                        @click="addNew = true"
                    >
                        <span>Add New</span> <UserPlusIcon class="block h-6 w-5 pr-1" aria-hidden="true"/>
                    </button>
                </template>
                <template #default>
                    <LatteClientContacts
                        :create-client="isCreateMode"
                        :addNew="addNew"
                        :selectedContactId="selectedContactId"
                        @update:selected="() => { addNew = false }"
                        @update:clientData="isCreateMode ? rehydrateClient : refreshClient"
                        :enable="contactModuleEnable"
                        :smsEnabled="false"
                    />
                </template>
            </LatteStyledContainer>

            <!-- SECTION 4: WHO'S NEARBY -->
            <LatteContainer
                id="latte-map-nearby"
                title="Who's Nearby"
                :forceSingleLineMobileLayout="true"
            >
                <LatteWhosNearby
                    :useDefaultCenter="isCreateMode ? false : undefined"
                    :clientId="clientId || client.id"
                />
            </LatteContainer>

            <!-- SECTION 5: CARE NEEDS -->
            <LatteStyledContainer
                id="care_need"
                title="Care Needs"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
            >
                <template #default>
                    <LatteClientCareNeed
                        @client:update="updateClient"
                        @client:cancel-update="cancelClientUpdate"
                    />
                </template>
            </LatteStyledContainer>

            <!-- SECTION 6: CARE SCHEDULE (always visible, fully functional in both modes) -->
            <LatteStyledContainer
                id="schedule"
                title="Care Schedule"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
            >
                <template #header>
                    <div class="w-full flex justify-center items-center">
                        <WeekPicker
                            v-model="scheduleDate"
                            :disabled="scheduleBuilderMode == 'edit'"
                        />
                    </div>
                </template>
                <template #default>
                    <LatteClientSchedule
                        :client_id="clientId || Number(client.id) || null"
                        :start_date="scheduleDate"
                        @builder-mode:change="(mode) => scheduleBuilderMode = mode"
                    />
                </template>
            </LatteStyledContainer>

            <!-- SECTION 7: CAREGIVER FEEDBACK (always visible, empty in create mode) -->
            <LatteContainer
                id="feedback"
                title="Caregivers & Feedback"
                :forceSingleLineMobileLayout="true"
                :collapsible="false"
                :start="1"
                :end="1"
                :showRefreshButton="isEditMode"
                @refresh="rehydrateJobs"
            >
                <div>

                    <LatteFeedbackTableHeader />

                    <div
                        v-if="isCreateMode && !client.id"
                        class="py-8 text-center font-semibold text-zinc-400"
                    >
                        No caregiver feedback yet. Feedback will appear once jobs are assigned.
                    </div>

                    <div
                        v-else-if="loading"
                        class="py-36 bg-zinc-100"
                    >
                        <Loader />
                    </div>

                    <div
                        v-else-if="Object.keys(sortedJobsByClientAll).length == 0"
                        class="py-8 text-center font-semibold text-zinc-400"
                    >
                        No matching jobs found.
                    </div>

                    <div v-else>

                        <template
                            v-for="([clientIdVal, jobIdStartDates]) in sortedJobsByClientAllOrdered" :key="clientIdVal"
                        >

                            <LatteFeedbackTableClientRow
                                :client="client"
                                :jobs="sortedJobsByClientAll[clientIdVal]"
                            />

                            <div class="">
                                <template
                                    v-for="job in jobStore.jobsOrderedDesc(sortedJobsByClientAll[clientIdVal], jobIdStartDates)" :key="job.id"
                                >
                                    <LatteFeedbackTableCaregiverRow
                                        v-if="caregivers[job.caregiver.id]"
                                        :job="job"
                                        :caregiver="caregivers[job.caregiver.id]"
                                    />
                                </template>
                            </div>

                            <div class="w-full h-1 bg-zinc-300"></div>

                        </template>

                    </div>

                </div>

            </LatteContainer>

            <!-- SECTION 8: DOCUMENTS (always visible if feature enabled, fully functional in both modes) -->
            <LatteStyledContainer
                v-if="configStore.featureEnabled('client-document')"
                id="documents"
                title="Documents"
                :collapsible="true"
                :forceSingleLineMobileLayout="true"
                :centerHeaderSlot="false"
            >
                <template #header-actions>
                    <button
                        @click="docsRef && docsRef.startDocumentsTour()"
                        class="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                        title="Start documents tour"
                    >
                        <IconQuestionCircle12Filled class="size-5 text-white" />
                    </button>
                </template>
                <template #header>
                    <template v-if="docsRef && docsRef.signatureRequestOpen">
                        <ChevronRightIcon class="-ml-4 flex mt-0.5 size-5 mr-2 text-white justify-center" aria-hidden="true"/> <span class="text-white font-semibold select-none">New Signature Request</span>
                        <IconDismissCircle20Regular class="ml-2 flex size-5 text-white justify-center cursor-pointer" aria-hidden="true" @click="docsRef.closeSignatureRequestDialog()" />
                    </template>

                    <button
                        v-show="!docsRef || !docsRef.signatureRequestOpen"
                        id="client-profile-documents-module-header-create-request-button"
                        ref="createSignatureRequestButton"
                        class="min-h-8 rounded bg-white py-1 flex space-x-1 items-center text-xs font-bold px-4"
                        @click="docsRef.openSignatureRequestDialog()"
                    >
                        <IconSignature20Filled class="block size-5 pr-1" aria-hidden="true"/> <span>Create Signature Request</span>
                    </button>

                </template>
                <template #default>
                    <LatteClientDocuments
                        ref="docsRef"
                        :create-signature-request-button-ref="createSignatureRequestButton"
                        @missing-primary-email-confirmation-closed="scrollToContactAndSelect($event)"
                    />
                </template>
                <template #below>
                    <LatteClientDocumentsUploadDrawer :upload-state="docsRef" />
                </template>
            </LatteStyledContainer>

        </div>
    </div>

</template>
